import { LogOut, Moon, Search, Sun, Sunrise } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/hooks/useTheme';
import { useAuth } from '@/hooks/useAuth';
import { useDataFreshness } from '@/hooks/useDataFreshness';
import { OutletSelector } from '@/components/OutletSelector';
import { PeriodPicker } from '@/components/PeriodPicker';
import { LivePulse } from '@/components/charts/LivePulse';

export function Topbar() {
  const { theme, resolved, setTheme } = useTheme();
  const { user, signOut } = useAuth();
  const { data: minutesOld } = useDataFreshness();

  return (
    <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur md:px-6">
      <OutletSelector />
      <PeriodPicker className="hidden sm:inline-flex" />
      <div className="ml-auto flex items-center gap-2">
        {typeof minutesOld === 'number' && (
          <div className="hidden items-center gap-1.5 rounded-md border border-border/60 bg-card/60 px-2 py-1 text-2xs text-muted-foreground sm:flex">
            <LivePulse ageMinutes={minutesOld} />
            {minutesOld < 60 ? `Live · ${minutesOld}m` : `Stale · ${Math.round(minutesOld / 60)}h`}
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          className="hidden text-2xs text-muted-foreground sm:inline-flex"
          onClick={() => {
            const evt = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
            window.dispatchEvent(evt);
          }}
        >
          <Search className="h-3 w-3" />
          Search
          <kbd className="ml-1 rounded border border-border/60 bg-background px-1 py-0.5 text-2xs">
            ⌘K
          </kbd>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            // Cycle: sun (auto) → light → dark → sun
            const next = theme === 'sun' ? 'light' : theme === 'light' ? 'dark' : 'sun';
            setTheme(next);
          }}
          aria-label={`Theme: ${theme === 'sun' ? `auto (sunset → dark)` : theme}`}
          title={
            theme === 'sun'
              ? 'Auto: light during the day, dark after sunset (Mumbai). Click for manual.'
              : theme === 'dark'
                ? 'Dark mode. Click for auto sunset.'
                : 'Light mode. Click for dark.'
          }
        >
          {theme === 'sun' ? (
            <Sunrise className="h-4 w-4" />
          ) : resolved === 'dark' ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </Button>
        {user && (
          <div className="hidden items-center gap-2 border-l border-border/60 pl-2 sm:flex">
            <span
              className="text-2xs text-muted-foreground"
              title={user.email ?? undefined}
            >
              {user.email?.split('@')[0]}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={signOut}
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
